import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Search, Kanban, PenLine, FolderClosed,
  Sparkles, LogOut, Settings, Bookmark, CalendarDays
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/grants", label: "Discover Grants", icon: Search, testid: "nav-grants" },
  { to: "/saved", label: "Saved", icon: Bookmark, testid: "nav-saved" },
  { to: "/applications", label: "Applications", icon: Kanban, testid: "nav-applications" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/ai-writer", label: "AI Grant Writer", icon: PenLine, testid: "nav-ai-writer" },
  { to: "/documents", label: "Documents", icon: FolderClosed, testid: "nav-documents" },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <aside className="w-[260px] shrink-0 h-screen sticky top-0 bg-[#F9F8F6] border-r border-[#E6E4DF] flex flex-col"
           data-testid="app-sidebar">
      <div className="p-6 border-b border-[#E6E4DF]">
        <button
          onClick={() => navigate("/")}
          data-testid="sidebar-logo"
          className="flex items-center gap-2.5 group"
        >
          <div className="w-9 h-9 rounded-md bg-[#1C3F35] flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#F9F8F6]" strokeWidth={2.5} />
          </div>
          <div className="text-left">
            <div className="font-heading font-medium text-lg leading-none tracking-tight text-[#1A1A1A]">Beacon of</div>
            <div className="label-eyebrow mt-1">Grants</div>
          </div>
        </button>
      </div>

      <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
        {NAV.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all
              ${isActive
                ? "bg-[#1C3F35] text-white font-medium"
                : "text-[#1A1A1A] hover:bg-[#E8ECEB]"}`
            }
          >
            <Icon className="w-4 h-4" strokeWidth={1.75} />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-[#E6E4DF] space-y-3">
        <div className="px-2 py-2">
          <div className="text-xs text-[#5E5E5E]">Signed in as</div>
          <div className="text-sm font-medium text-[#1A1A1A] truncate" data-testid="sidebar-user-name">
            {user?.full_name}
          </div>
          <div className="text-xs text-[#5E5E5E] truncate">{user?.email}</div>
          <div className="mt-2 inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#E8ECEB] text-[#1C3F35]"
               data-testid="sidebar-tier">
            {user?.subscription_tier || "free"}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="flex-1 justify-start"
            onClick={() => navigate("/settings")}
            data-testid="nav-settings"
          >
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            data-testid="nav-logout"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </aside>
  );
}
